import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, KeyRound, UserCog, Check, X } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const PASSWORD_RULES = [
  { key: "length", label: "At least 10 characters", test: (v) => v.length >= 10 },
  { key: "upper", label: "One uppercase letter (A–Z)", test: (v) => /[A-Z]/.test(v) },
  { key: "lower", label: "One lowercase letter (a–z)", test: (v) => /[a-z]/.test(v) },
  { key: "digit", label: "One number (0–9)", test: (v) => /[0-9]/.test(v) },
  {
    key: "special",
    label: "One special character (e.g. ! @ # $ %)",
    test: (v) => /[^A-Za-z0-9]/.test(v),
  },
];

function Rule({ ok, label }) {
  const Icon = ok ? Check : X;
  return (
    <li className="flex items-center gap-2 text-xs" data-testid={`rule-${ok ? "pass" : "fail"}`}>
      <Icon className={ok ? "size-3.5 text-emerald-600" : "size-3.5 text-muted-foreground"} />
      <span className={ok ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"}>
        {label}
      </span>
    </li>
  );
}

export default function AccountSettingsPage() {
  const { admin, logout, updateAdmin } = useAuth();

  const [profile, setProfile] = useState({
    name: admin?.name || "",
    email: admin?.email || "",
  });
  const [savingProfile, setSavingProfile] = useState(false);

  const [pwd, setPwd] = useState({ current: "", next: "", confirm: "" });
  const [savingPwd, setSavingPwd] = useState(false);

  const rules = PASSWORD_RULES.map((r) => ({ ...r, ok: r.test(pwd.next) }));
  const allRulesPass = rules.every((r) => r.ok);
  const matches = pwd.next && pwd.next === pwd.confirm;

  const onSaveProfile = async (e) => {
    e.preventDefault();
    const payload = {};
    if (profile.name.trim() && profile.name !== admin?.name) payload.name = profile.name.trim();
    if (profile.email.trim() && profile.email !== admin?.email) payload.email = profile.email.trim();
    if (Object.keys(payload).length === 0) {
      toast.info("Nothing to update");
      return;
    }
    setSavingProfile(true);
    try {
      const { data } = await api.patch("/admin/profile", payload);
      updateAdmin({ name: data.name, email: data.email });
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err.userMessage);
    } finally {
      setSavingProfile(false);
    }
  };

  const onChangePassword = async (e) => {
    e.preventDefault();
    if (!pwd.current) {
      toast.error("Enter your current password");
      return;
    }
    if (!allRulesPass) {
      toast.error("New password does not meet the requirements");
      return;
    }
    if (!matches) {
      toast.error("New password and confirmation do not match");
      return;
    }
    setSavingPwd(true);
    try {
      await api.post("/admin/change-password", {
        current_password: pwd.current,
        new_password: pwd.next,
      });
      setPwd({ current: "", next: "", confirm: "" });
      toast.success("Password changed successfully");
    } catch (err) {
      toast.error(err.userMessage);
    } finally {
      setSavingPwd(false);
    }
  };

  return (
    <>
      <PageHeader
        title="Account Settings"
        description="Manage your admin profile and password."
      />

      <div className="grid gap-5 lg:grid-cols-2">
        {/* Profile */}
        <form onSubmit={onSaveProfile}>
          <Card className="border-border/70 shadow-sm h-full" data-testid="account-profile-card">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <UserCog className="size-4 text-muted-foreground" />
                Profile
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                Update how your name and email appear across the admin console.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="account-name">Display name</Label>
                <Input
                  id="account-name"
                  value={profile.name}
                  onChange={(e) => setProfile({ ...profile, name: e.target.value })}
                  data-testid="account-name-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="account-email">Email</Label>
                <Input
                  id="account-email"
                  type="email"
                  value={profile.email}
                  onChange={(e) => setProfile({ ...profile, email: e.target.value })}
                  data-testid="account-email-input"
                />
              </div>
              <div className="text-xs text-muted-foreground">
                Role: <span className="font-medium text-foreground">{admin?.role || "—"}</span>
              </div>
              <div className="flex justify-end pt-1">
                <Button
                  type="submit"
                  disabled={savingProfile}
                  data-testid="account-profile-save-button"
                >
                  {savingProfile ? (
                    <Loader2 className="size-4 mr-1.5 animate-spin" />
                  ) : (
                    <Save className="size-4 mr-1.5" />
                  )}
                  Save profile
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>

        {/* Password */}
        <form onSubmit={onChangePassword}>
          <Card className="border-border/70 shadow-sm h-full" data-testid="account-password-card">
            <CardHeader>
              <CardTitle className="text-base font-semibold flex items-center gap-2">
                <KeyRound className="size-4 text-muted-foreground" />
                Change password
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                You'll stay signed in on this device after changing your password.
              </p>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="pwd-current">Current password</Label>
                <Input
                  id="pwd-current"
                  type="password"
                  autoComplete="current-password"
                  value={pwd.current}
                  onChange={(e) => setPwd({ ...pwd, current: e.target.value })}
                  data-testid="pwd-current-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pwd-next">New password</Label>
                <Input
                  id="pwd-next"
                  type="password"
                  autoComplete="new-password"
                  value={pwd.next}
                  onChange={(e) => setPwd({ ...pwd, next: e.target.value })}
                  data-testid="pwd-new-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pwd-confirm">Confirm new password</Label>
                <Input
                  id="pwd-confirm"
                  type="password"
                  autoComplete="new-password"
                  value={pwd.confirm}
                  onChange={(e) => setPwd({ ...pwd, confirm: e.target.value })}
                  data-testid="pwd-confirm-input"
                />
                {pwd.confirm && !matches && (
                  <p className="text-xs text-destructive" data-testid="pwd-mismatch">
                    Passwords do not match
                  </p>
                )}
              </div>

              <ul className="rounded-lg border border-border/70 bg-muted/30 px-3 py-2.5 space-y-1">
                {rules.map((r) => (
                  <Rule key={r.key} ok={r.ok} label={r.label} />
                ))}
              </ul>

              <div className="flex justify-end pt-1">
                <Button
                  type="submit"
                  disabled={savingPwd || !allRulesPass || !matches || !pwd.current}
                  data-testid="pwd-submit-button"
                >
                  {savingPwd ? (
                    <Loader2 className="size-4 mr-1.5 animate-spin" />
                  ) : (
                    <KeyRound className="size-4 mr-1.5" />
                  )}
                  Update password
                </Button>
              </div>
            </CardContent>
          </Card>
        </form>
      </div>

      <div className="mt-6 flex justify-end">
        <Button
          type="button"
          variant="outline"
          onClick={logout}
          data-testid="account-logout-button"
        >
          Sign out
        </Button>
      </div>
    </>
  );
}
